import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { User, Lock, Eye, EyeOff, Eye as EyeIcon, EyeOff as EyeOffIcon, UserPlus, MapPin, Phone, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { buildApiUrl } from '../../config/api';
import AddressAutocomplete from '../AddressAutocomplete';
import {
  US_STATES_48,
  formatUsPhoneInput,
  formatUsZipInput,
  isValidUsPhone,
  isValidUsState,
  isValidUsZip,
  normalizeUsStateCode
} from '../../utils/usContactAddress';

const CreateAccountContainer = styled.div`
  padding: 20px 22px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const SuccessView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  flex: 1;
`;

const SuccessIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: #f0fdf4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #22c55e;
  margin-bottom: 24px;
`;

const SuccessTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1a2b48;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TopLabel = styled.span`
  color: #7a8ba0;
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 400;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  background: #f1f5f9;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  color: #1a2b48;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0 auto;
  color: #778394ff;
  font-size: 16px;
  max-width: 80%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #1a2b48;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #5d7231;
    box-shadow: 0 0 0 3px rgba(93, 114, 49, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordFieldInput = styled(Input)`
  padding-right: 44px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 36px 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background-color: #fff;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #5d7231;
    box-shadow: 0 0 0 3px rgba(93, 114, 49, 0.12);
  }
`;

const SelectWithLeadingIcon = styled(Select)`
  padding-left: 44px;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: #7a8ba0;
  line-height: 1.4;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 14px;
  color: #9ca3af;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    color: #6b7280;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #7a8ba0;
  cursor: pointer;
  line-height: 1.45;
`;

const Checkbox = styled.input`
  margin: 0;
  margin-top: 2px;
  cursor: pointer;
`;

const CreateButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 8px;
  background: #5d7231;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);

  &:hover:not(:disabled) {
    background: #4d5f28;
    box-shadow: 0 4px 12px rgba(93, 114, 49, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 22px 0 14px 0;
  padding-top: 18px;
  border-top: 1px solid #e2e8f0;
`;

const SignInText = styled.p`
  margin: 0 0 12px 0;
  color: #7a8ba0;
  font-size: 14px;
`;

const SignInLink = styled.button`
  background: none;
  border: none;
  color: #5d7231;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #4d5f28;
  }
`;

const OtpSection = styled.div`
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #f7fafc;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OtpInputs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const SecondaryButton = styled.button`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 140px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OtpActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

interface CreateAccountProps {
  onSwitchToLogin: (opts?: { forgotPassword?: boolean; email?: string }) => void;
  onClose?: () => void;
}

const CreateAccount: React.FC<CreateAccountProps> = ({ onSwitchToLogin, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    userName: '',
    companyName: '',
    accountType: 'company',
    role: 'customer',
    dealerCustomerCode: '',
    city: '',
    state: '',
    addressLine1: '',
    addressLine2: '',
    zipCode: '',
    // Shipping Address
    sameAsBilling: true,
    shipAddressLine1: '',
    shipAddressLine2: '',
    shipCity: '',
    shipState: '',
    shipZipCode: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pendingOtp, setPendingOtp] = useState<{
    token: string;
    expiresAt: string | null;
    channels?: { email?: string | null; phone?: string | null };
  } | null>(null);
  const [otpCodes, setOtpCodes] = useState({ email: '', phone: '' });
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [duplicateAccount, setDuplicateAccount] = useState(false);
  const [otpResendCount, setOtpResendCount] = useState(0);
  const [otpLastResendAt, setOtpLastResendAt] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const handleInputChange = (field: string, value: any) => {
    if (field === 'email') {
      setDuplicateAccount(false);
    }
    let nextValue = value;
    if (field === 'zipCode' || field === 'shipZipCode') {
      nextValue = formatUsZipInput(value);
    } else if (field === 'state' || field === 'shipState') {
      nextValue = normalizeUsStateCode(value);
    }
    setFormData(prev => {
      if (field === 'phone') {
        nextValue = formatUsPhoneInput(value, prev.phone);
      }
      const next = { ...prev, [field]: nextValue };
      if (field === 'accountType' && value === 'individual') {
        next.companyName = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setOtpError(null);

    const isCustomer = formData.role === 'customer';

    if (!formData.userName.trim()) {
      setErrorMessage('User name is required');
      return;
    }

    if (!formData.phone.trim()) {
      setErrorMessage('Phone number is required');
      return;
    }
    if (!isValidUsPhone(formData.phone)) {
      setErrorMessage('Phone must use format +1 (706) 123-4567');
      return;
    }

    if (!formData.email.trim()) {
      setErrorMessage('Email is required');
      return;
    }

    if (isCustomer && !formData.companyName.trim()) {
      setErrorMessage('Company name is required');
      return;
    }

    if (isCustomer) {
      if (!formData.addressLine1.trim()) {
        setErrorMessage('Billing address line 1 is required');
        return;
      }
      if (!formData.city.trim() || !formData.state.trim() || !formData.zipCode.trim()) {
        setErrorMessage('Billing City, State, and ZIP Code are required');
        return;
      }
      if (!isValidUsState(formData.state)) {
        setErrorMessage('Please choose a valid U.S. billing state');
        return;
      }
      if (!isValidUsZip(formData.zipCode)) {
        setErrorMessage('Billing ZIP code must be exactly 5 digits');
        return;
      }

      if (!formData.sameAsBilling) {
        if (!formData.shipAddressLine1.trim()) {
          setErrorMessage('Shipping address line 1 is required');
          return;
        }
        if (!formData.shipCity.trim() || !formData.shipState.trim() || !formData.shipZipCode.trim()) {
          setErrorMessage('Shipping City, State, and ZIP Code are required');
          return;
        }
        if (!isValidUsState(formData.shipState)) {
          setErrorMessage('Please choose a valid U.S. shipping state');
          return;
        }
        if (!isValidUsZip(formData.shipZipCode)) {
          setErrorMessage('Shipping ZIP code must be exactly 5 digits');
          return;
        }
      }
    } else {
      // Salesperson role requires Dealer Customer Code and Password
      if (!formData.dealerCustomerCode.trim()) {
        setErrorMessage('Dealer Customer Code is required');
        return;
      }
      if (!formData.password || formData.password.length < 6) {
        setErrorMessage('Password is required (minimum 6 characters)');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setErrorMessage('Passwords do not match');
        return;
      }
    }

    setSubmitting(true);
    setDuplicateAccount(false);
    try {
      const shipZip = isCustomer ? (formData.sameAsBilling ? formData.zipCode.trim() : formData.shipZipCode.trim()) : '';

      const response = await fetch(buildApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: isCustomer ? shipZip : formData.password,
          first_name: formData.firstName,
          middle_name: formData.middleName.trim(),
          last_name: formData.lastName,
          user_name: formData.userName,
          company_name: isCustomer ? formData.companyName.trim() : null,
          account_type: isCustomer ? 'company' : 'individual',
          role: formData.role,
          dealer_customer_code: formData.role === 'dealer_salesperson' ? formData.dealerCustomerCode.trim().toUpperCase() : null,
          city: isCustomer ? formData.city : '',
          state: isCustomer ? normalizeUsStateCode(formData.state) : '',
          address_line1: isCustomer ? formData.addressLine1 : '',
          address_line2: isCustomer ? formData.addressLine2 : '',
          zip_code: isCustomer ? formData.zipCode.trim() : '',
          same_as_billing: isCustomer ? formData.sameAsBilling : true,
          ship_address_line1: isCustomer ? (formData.sameAsBilling ? formData.addressLine1 : formData.shipAddressLine1) : '',
          ship_address_line2: isCustomer ? (formData.sameAsBilling ? formData.addressLine2 : formData.shipAddressLine2) : '',
          ship_city: isCustomer ? (formData.sameAsBilling ? formData.city : formData.shipCity) : '',
          ship_state: isCustomer ? (formData.sameAsBilling ? normalizeUsStateCode(formData.state) : normalizeUsStateCode(formData.shipState)) : '',
          ship_zip_code: shipZip,
          phone: formData.phone.trim()
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        const message = (data && (data.error || data.message)) || 'Registration failed';
        const conflict =
          response.status === 409 ||
          /already|in use/i.test(String(data.error || data.message || message));
        setDuplicateAccount(conflict);
        throw new Error(message);
      }
      if (data.requires_otp) {
        setPendingOtp({
          token: data.pending_token,
          expiresAt: data.expires_at || null,
          channels: data.channels || {},
        });
        setOtpMessage(
          data.message ||
          'We sent a verification code to your email. Enter it to finish creating your account.'
        );
        return;
      }
      setSuccessMessage(data.customer_id
        ? `Account created successfully. Customer ID: ${data.customer_id}`
        : 'Account created successfully. You can now sign in.');
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        userName: '',
        companyName: '',
        accountType: 'individual',
        role: 'customer',
        dealerCustomerCode: '',
        city: '',
        state: '',
        addressLine1: '',
        addressLine2: '',
        zipCode: '',
        sameAsBilling: true,
        shipAddressLine1: '',
        shipAddressLine2: '',
        shipCity: '',
        shipState: '',
        shipZipCode: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      setPendingOtp(null);
      setOtpCodes({ email: '', phone: '' });
      setDuplicateAccount(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingOtp) return;
    setOtpError(null);
    setVerifyingOtp(true);

    try {
      const response = await fetch(buildApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pending_token: pendingOtp.token,
          email_code: otpCodes.email,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        const message = (data && (data.error || data.message)) || 'Verification failed';
        throw new Error(message);
      }

      if (data && data.token) {
        try {
          const storage = window.sessionStorage;
          storage.setItem('auth_token', data.token);
          storage.setItem('auth_user', JSON.stringify(data.user || {}));
          try { window.dispatchEvent(new Event('auth:login')); } catch { }
        } catch { }
      }

      setIsSuccess(true);
      setPendingOtp(null);
      setOtpCodes({ email: '', phone: '' });
      setOtpMessage(null);
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        userName: '',
        companyName: '',
        accountType: 'individual',
        role: 'customer',
        dealerCustomerCode: '',
        city: '',
        state: '',
        addressLine1: '',
        addressLine2: '',
        zipCode: '',
        sameAsBilling: true,
        shipAddressLine1: '',
        shipAddressLine2: '',
        shipCity: '',
        shipState: '',
        shipZipCode: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setOtpError(message);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingOtp) return;

    // Check limit
    if (otpResendCount >= 3) {
      setOtpError('You have reached the maximum number of OTP resend attempts (3). Please try again later.');
      return;
    }

    // Check cooldown
    const now = Date.now();
    const elapsed = now - otpLastResendAt;
    if (elapsed < 60000) {
      const remaining = Math.ceil((60000 - elapsed) / 1000);
      setOtpError(`Please wait ${remaining} seconds before requesting a new code.`);
      return;
    }

    setResendingOtp(true);
    setOtpError(null);

    try {
      const response = await fetch(buildApiUrl('/api/auth/register/resend-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_token: pendingOtp.token }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.ok === false) {
        const message = (data && (data.error || data.message)) || 'Unable to resend codes';
        throw new Error(message);
      }

      setOtpResendCount(prev => prev + 1);
      setOtpLastResendAt(Date.now());
      setCooldownRemaining(60);
      setOtpMessage(`A new verification code has been sent. Attempt ${otpResendCount + 1} of 3.`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unexpected error occurred';
      setOtpError(message);
    } finally {
      setResendingOtp(false);
    }
  };

  // Cooldown timer
  useEffect(() => {
    let timer: any;
    if (cooldownRemaining > 0) {
      timer = setInterval(() => {
        setCooldownRemaining(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  return (
    <CreateAccountContainer>
      {isSuccess ? (
        <SuccessView>
          <SuccessIconWrapper>
            <CheckCircle size={48} />
          </SuccessIconWrapper>
          <SuccessTitle>Application Received</SuccessTitle>
          <SuccessMessage>
            We sell only to wholesale death care related businesses. We will contact you within 1 business day after we verify and create your account access.
          </SuccessMessage>
          <CreateButton onClick={onClose || (() => window.location.reload())}>
            Back to Home
          </CreateButton>
        </SuccessView>
      ) : (
        <>
          <Header>
            {onClose && (
              <BackButton onClick={onClose}>
                <ArrowLeft size={20} />
              </BackButton>
            )}
            <TopLabel>Save and manage templates</TopLabel>
            <Title>Create Account</Title>
            <Subtitle>
              We create accounts only for authorized death care related businesses like Funeral Homes, Cemeteries and Monument Dealers.
            </Subtitle>
          </Header>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="role">Account Role</Label>
              <InputContainer>
                <IconWrapper>
                  <User size={20} />
                </IconWrapper>
                <SelectWithLeadingIcon
                  id="role"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  required
                >
                  <option value="customer">Customer / Dealer Owner</option>
                  <option value="dealer_salesperson">Dealer Salesperson</option>
                </SelectWithLeadingIcon>
              </InputContainer>
            </InputGroup>

            {formData.role === 'customer' ? (
              <InputGroup>
                <Label htmlFor="companyName">Company Name</Label>
                <InputContainer>
                  <IconWrapper>
                    <User size={20} />
                  </IconWrapper>
                  <Input
                    id="companyName"
                    type="text"
                    placeholder="Company or organization name"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    autoComplete="one-time-code"
                    required
                  />
                </InputContainer>
              </InputGroup>
            ) : (
              <InputGroup>
                <Label htmlFor="dealerCustomerCode">Dealer Customer Code</Label>
                <InputContainer>
                  <IconWrapper>
                    <User size={20} />
                  </IconWrapper>
                  <Input
                    id="dealerCustomerCode"
                    type="text"
                    placeholder="e.g. CECTCA (ask your Dealer Owner)"
                    value={formData.dealerCustomerCode}
                    onChange={(e) => handleInputChange('dealerCustomerCode', e.target.value.toUpperCase())}
                    autoComplete="one-time-code"
                    required
                  />
                </InputContainer>
              </InputGroup>
            )}

            <InputGroup>
              <Label htmlFor="firstName">First Name</Label>
              <InputContainer>
                <IconWrapper>
                  <User size={20} />
                </IconWrapper>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="middleName">Middle Name</Label>
              <InputContainer>
                <IconWrapper>
                  <User size={20} />
                </IconWrapper>
                <Input
                  id="middleName"
                  type="text"
                  placeholder="Middle (optional)"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  autoComplete="one-time-code"
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <InputContainer>
                <IconWrapper>
                  <User size={20} />
                </IconWrapper>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="userName">User Name</Label>
              <InputContainer>
                <IconWrapper>
                  <User size={20} />
                </IconWrapper>
                <Input
                  id="userName"
                  type="text"
                  placeholder="Preferred user name"
                  value={formData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </InputContainer>
            </InputGroup>

            {formData.role === 'customer' && (
              <>
                <InputGroup style={{ marginBottom: -8, marginTop: 4 }}>
                  <Label style={{ fontSize: '15px', color: '#5d7231', fontWeight: 'bold' }}>Billing Address</Label>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="addressLine1">Address Line 1</Label>
                  <AddressAutocomplete
                    id="addressLine1"
                    placeholder="Street address (start typing to see suggestions)"
                    value={formData.addressLine1}
                    onInputChange={(val) => handleInputChange('addressLine1', val)}
                    onAddressSelect={(details) => {
                      setFormData(prev => ({
                        ...prev,
                        addressLine1: details.address_line1,
                        city: details.city,
                        state: normalizeUsStateCode(details.state),
                        zipCode: formatUsZipInput(details.zip_code)
                      }));
                    }}
                    required
                  />
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
                  <InputContainer>
                    <IconWrapper>
                      <MapPin size={20} />
                    </IconWrapper>
                    <Input
                      id="addressLine2"
                      type="text"
                      placeholder="Apt, suite, unit, etc."
                      value={formData.addressLine2}
                      onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                      autoComplete="one-time-code"
                    />
                  </InputContainer>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="city">City</Label>
                  <InputContainer>
                    <IconWrapper>
                      <MapPin size={20} />
                    </IconWrapper>
                    <Input
                      id="city"
                      type="text"
                      placeholder="City"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      autoComplete="one-time-code"
                      required
                    />
                  </InputContainer>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="state">State</Label>
                  <InputContainer>
                    <IconWrapper>
                      <MapPin size={20} />
                    </IconWrapper>
                    <SelectWithLeadingIcon
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      required
                    >
                      <option value="">Select state</option>
                      {US_STATES_48.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </option>
                      ))}
                    </SelectWithLeadingIcon>
                  </InputContainer>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <InputContainer>
                    <IconWrapper>
                      <MapPin size={20} />
                    </IconWrapper>
                    <Input
                      id="zipCode"
                      type="text"
                      placeholder="12345"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={5}
                      required
                    />
                  </InputContainer>
                </InputGroup>

                <InputGroup style={{ marginBottom: -8, marginTop: 12 }}>
                  <Label style={{ fontSize: '15px', color: '#5d7231', fontWeight: 'bold' }}>Shipping Address</Label>
                </InputGroup>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 16px 0' }}>
                  <input
                    type="checkbox"
                    id="sameAsBilling"
                    checked={formData.sameAsBilling}
                    onChange={(e) => setFormData(prev => ({ ...prev, sameAsBilling: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#5d7231' }}
                  />
                  <label htmlFor="sameAsBilling" style={{ fontSize: 14, color: '#334155', cursor: 'pointer', fontWeight: 500 }}>
                    Shipping address is same as billing address
                  </label>
                </div>

                {!formData.sameAsBilling && (
                  <>
                    <InputGroup>
                      <Label htmlFor="shipAddressLine1">Shipping Address Line 1</Label>
                      <AddressAutocomplete
                        id="shipAddressLine1"
                        placeholder="Shipping street address"
                        value={formData.shipAddressLine1}
                        onInputChange={(val) => handleInputChange('shipAddressLine1', val)}
                        onAddressSelect={(details) => {
                          setFormData(prev => ({
                            ...prev,
                            shipAddressLine1: details.address_line1,
                            shipCity: details.city,
                            shipState: normalizeUsStateCode(details.state),
                            shipZipCode: formatUsZipInput(details.zip_code)
                          }));
                        }}
                        required
                      />
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="shipAddressLine2">Shipping Address Line 2 (Optional)</Label>
                      <InputContainer>
                        <IconWrapper>
                          <MapPin size={20} />
                        </IconWrapper>
                        <Input
                          id="shipAddressLine2"
                          type="text"
                          placeholder="Apt, suite, unit, etc."
                          value={formData.shipAddressLine2}
                          onChange={(e) => handleInputChange('shipAddressLine2', e.target.value)}
                          autoComplete="one-time-code"
                        />
                      </InputContainer>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="shipCity">Shipping City</Label>
                      <InputContainer>
                        <IconWrapper>
                          <MapPin size={20} />
                        </IconWrapper>
                        <Input
                          id="shipCity"
                          type="text"
                          placeholder="Shipping City"
                          value={formData.shipCity}
                          onChange={(e) => handleInputChange('shipCity', e.target.value)}
                          autoComplete="one-time-code"
                          required
                        />
                      </InputContainer>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="shipState">Shipping State</Label>
                      <InputContainer>
                        <IconWrapper>
                          <MapPin size={20} />
                        </IconWrapper>
                        <SelectWithLeadingIcon
                          id="shipState"
                          value={formData.shipState}
                          onChange={(e) => handleInputChange('shipState', e.target.value)}
                          required
                        >
                          <option value="">Select state</option>
                          {US_STATES_48.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.code} - {state.name}
                            </option>
                          ))}
                        </SelectWithLeadingIcon>
                      </InputContainer>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="shipZipCode">Shipping ZIP Code</Label>
                      <InputContainer>
                        <IconWrapper>
                          <MapPin size={20} />
                        </IconWrapper>
                        <Input
                          id="shipZipCode"
                          type="text"
                          placeholder="12345"
                          value={formData.shipZipCode}
                          onChange={(e) => handleInputChange('shipZipCode', e.target.value)}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          maxLength={5}
                          required
                        />
                      </InputContainer>
                    </InputGroup>
                  </>
                )}
              </>
            )}

            <InputGroup>
              <Label htmlFor="phone">Phone</Label>
              <InputContainer>
                <IconWrapper>
                  <Phone size={20} />
                </IconWrapper>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (706) 123-4567"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
              </InputContainer>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="email">Email</Label>
              <InputContainer>
                <IconWrapper>
                  <Mail size={20} />
                </IconWrapper>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </InputContainer>
            </InputGroup>

            {formData.role === 'dealer_salesperson' && (
              <>
                <InputGroup>
                  <Label htmlFor="password">Password</Label>
                  <InputContainer>
                    <IconWrapper>
                      <Lock size={20} />
                    </IconWrapper>
                    <PasswordFieldInput
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </PasswordToggle>
                  </InputContainer>
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <InputContainer>
                    <IconWrapper>
                      <Lock size={20} />
                    </IconWrapper>
                    <PasswordFieldInput
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      required
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </PasswordToggle>
                  </InputContainer>
                </InputGroup>
              </>
            )}


            {errorMessage && (
              <div style={{ color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
                {errorMessage}
              </div>
            )}

            {duplicateAccount && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <span style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#718096' }}>
                  This email may already be registered.
                </span>
                <SignInLink
                  type="button"
                  onClick={() =>
                    onSwitchToLogin({ forgotPassword: true, email: formData.email.trim() })
                  }
                >
                  Having difficulty signing in? Reset your password
                </SignInLink>
              </div>
            )}

            {otpError && (
              <div style={{ color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
                {otpError}
              </div>
            )}

            {otpMessage && (
              <div style={{ color: '#5d7231', fontSize: 14, textAlign: 'center' }}>
                {otpMessage}
              </div>
            )}

            {pendingOtp && (
              <div style={{
                marginTop: 10,
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <InputGroup>
                  <Label htmlFor="otpCode">Verification Code</Label>
                  <InputContainer>
                    <IconWrapper>
                      <Lock size={20} />
                    </IconWrapper>
                    <Input
                      id="otpCode"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit code"
                      value={otpCodes.email}
                      onChange={(e) => setOtpCodes((prev) => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </InputContainer>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      Didn't receive the code?
                    </span>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendingOtp || cooldownRemaining > 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: (resendingOtp || cooldownRemaining > 0) ? '#94a3b8' : '#5d7231',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: (resendingOtp || cooldownRemaining > 0) ? 'not-allowed' : 'pointer',
                        padding: '0'
                      }}
                    >
                      {resendingOtp
                        ? 'Sending...'
                        : cooldownRemaining > 0
                          ? `Resend in ${cooldownRemaining}s`
                          : 'Resend Code'}
                    </button>
                  </div>
                </InputGroup>
              </div>
            )}

            {!pendingOtp ? (
              <CreateButton type="submit" disabled={submitting}>
                {submitting ? 'Creating Account…' : 'Create Account'}
              </CreateButton>
            ) : (
              <CreateButton type="button" onClick={handleOtpSubmit} disabled={verifyingOtp}>
                {verifyingOtp ? 'Verifying…' : 'Verify & Create Account'}
              </CreateButton>
            )}

            <Divider>
              <SignInText>Already have an account?</SignInText>
              <SignInLink type="button" onClick={() => onSwitchToLogin()}>
                Sign In Now
              </SignInLink>
            </Divider>
          </Form>
        </>
      )}
    </CreateAccountContainer>
  );
};

export default CreateAccount;
